import { create } from "zustand";
import { generateClient, type GraphQLResult } from "aws-amplify/api";
import { GraphQLError } from "graphql";
import type { RestaurantsList } from "@/API";
import { getRestaurantsInGrid as getRestaurantsInGridQuery } from "@/graphql/queries";

const client = generateClient();

export type GetRestaurantsInGridState = {
  data: RestaurantsList | null;
  isLoading: boolean;
  error: GraphQLError[] | null;
  isFetching: boolean;
  _currentOffset: number;
};

export const getRestaurantsInGridState = create<GetRestaurantsInGridState>(
  () => ({
    data: null,
    isLoading: false,
    error: null,
    isFetching: false,
    _currentOffset: 0,
  }),
);

export const resetRestaurantsInGridState = () =>
  getRestaurantsInGridState.setState(
    getRestaurantsInGridState.getInitialState(),
  );

type GetRestaurantsInGridParams = {
  gridId: string;
};

export const getRestaurantsInGrid = async ({
  gridId,
}: GetRestaurantsInGridParams) => {
  const state = getRestaurantsInGridState.getState();
  if (state.isLoading || state.isFetching) return;

  getRestaurantsInGridState.setState({
    data: null,
    isLoading: true,
    isFetching: false,
    error: null,
    _currentOffset: 0,
  });

  let offset = 0;
  let total = Infinity;
  let accumulatedData: RestaurantsList["data"] = [];

  while (offset < total) {
    try {
      const { data } = await client.graphql({
        query: getRestaurantsInGridQuery,
        variables: { gridId, offset },
      });

      const incoming = data.getRestaurantsInGrid as RestaurantsList;
      total = incoming.total;
      accumulatedData = [...accumulatedData, ...incoming.data];
      offset = accumulatedData.length;

      getRestaurantsInGridState.setState({
        data: { data: accumulatedData, total } as RestaurantsList,
        _currentOffset: offset,
        isLoading: false,
        isFetching: offset < total,
      });
    } catch (e) {
      const graphQl = e as GraphQLResult<RestaurantsList>;
      getRestaurantsInGridState.setState({
        error: graphQl.errors ?? null,
        isLoading: false,
        isFetching: false,
      });
      break;
    }
  }
};
